import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Star, 
  Car, 
  Sparkles, 
  Shield, 
  Phone, 
  Mail,
  Clock, 
  CheckCircle, 
  Wrench, 
  Settings,
  Eye,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import GoogleReviews from '../components/GoogleReviews';
import LoadingSpinner, { LoadingCard, LoadingGalleryCard } from '../components/LoadingSpinner';
import { ErrorCard } from '../components/ErrorMessage';
import { useAPI } from '../hooks/useAPI';
import { servicesAPI, galleryAPI, pricingAPI } from '../services/api';

const HomePage = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  // API calls pour les données
  const { 
    data: services, 
    loading: servicesLoading, 
    error: servicesError,
    refetch: refetchServices 
  } = useAPI(() => servicesAPI.getAll(), []);

  const { 
    data: allPricing, 
    loading: pricingLoading, 
    error: pricingError,
    refetch: refetchPricing 
  } = useAPI(() => pricingAPI.getAll());

  // Organiser les tarifs par catégorie
  const simpleServices = allPricing?.filter(p => p.category === 'simple')?.sort((a, b) => (a.order || 0) - (b.order || 0)) || [];
  const principalServices = allPricing?.filter(p => p.category === 'principal')?.sort((a, b) => (a.order || 0) - (b.order || 0)) || [];
  const specialServices = allPricing?.filter(p => p.category === 'special')?.sort((a, b) => (a.order || 0) - (b.order || 0)) || [];

  const ServiceCard = ({ service, featured = false }) => {
    const isDevis = service.price === 'Devis personnalisé';
    
    return (
      <Card className={`h-full group hover:shadow-xl transition-all duration-300 ${featured ? 'ring-2 ring-blue-600 scale-105' : ''}`}>
        <CardHeader className="relative">
          {featured && (
            <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white">
              Plus populaire
            </Badge>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-gray-600">{service.duration}</span>
            </div>
            {featured && <Star className="w-5 h-5 text-yellow-400 fill-current" />}
          </div>
          <CardTitle className="text-xl text-gray-900">{service.service}</CardTitle>
          <div className="flex items-baseline space-x-1">
            <span className={`text-3xl font-bold ${isDevis ? 'text-orange-600' : 'text-blue-600'}`}>
              {service.price}
            </span>
            {service.price.includes('partir') && <span className="text-sm text-gray-500">*</span>}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <ul className="space-y-3 mb-6">
            {service.includes.map((item, index) => (
              <li key={index} className="flex items-start space-x-3">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-600">{item}</span>
              </li>
            ))}
          </ul>
          
          {isDevis ? (
            <div className="space-y-2">
              <Link to="/contact?sujet=devis">
                <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white group-hover:scale-105 transition-transform duration-200">
                  <Mail className="w-4 h-4 mr-2" />
                  Demander un devis
                </Button>
              </Link>
              <a href="tel:0622360190">
                <Button variant="outline" className="w-full text-orange-600 border-orange-600 hover:bg-orange-50">
                  <Phone className="w-4 h-4 mr-2" />
                  06 22 36 01 90
                </Button>
              </a>
            </div>
          ) : (
            <Link to={`/reservation?service=${encodeURIComponent(service.service)}`}>
              <Button className={`w-full ${featured ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-900 hover:bg-gray-800'} text-white group-hover:scale-105 transition-transform duration-200`}>
                Réserver ce service
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          )}
        </CardContent>
      </Card>
    );
  };

  const { 
    data: gallery, 
    loading: galleryLoading, 
    error: galleryError,
    refetch: refetchGallery 
  } = useAPI(() => galleryAPI.getAll(), []);

  // Prendre les 3 premières réalisations
  const featuredGallery = gallery?.slice(0, 3) || [];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-50 to-blue-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            {/* Logo supprimé comme demandé */}
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              <span className="text-blue-600">AutoSpa 77</span>
              <br />Centre de Detailing
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              Redonnez vie à votre véhicule avec nos services professionnels de detailing automobile. 
              Excellence, passion et expertise depuis plus de 10 ans en Seine-et-Marne.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/reservation">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg">
                  Réserver maintenant
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" size="lg" className="border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-4 text-lg">
                  Voir nos services
                </Button>
              </Link>
            </div>
            <div className="flex items-center justify-center mt-8 space-x-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
              ))}
              <span className="ml-2 text-gray-600">4.9/5 - Plus de 200 clients satisfaits</span>
            </div>
          </div>
        </div>
      </section>

      {/* Bandeau Photo */}
      <section className="relative w-full">
        <div className="relative w-full aspect-[21/9] md:aspect-[3/1] lg:aspect-[4/1] max-h-[500px]">
          <img 
            src="https://customer-assets.emergentagent.com/job_detailing-portal/artifacts/w2c8p7li_shutterstock_2287564617.jpg"
            alt="Voiture de luxe detailing"
            className="w-full h-full object-cover object-[center_30%]"
          />
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white px-4">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                L'Excellence du Detailing
              </h2>
              <p className="text-lg md:text-xl lg:text-2xl text-gray-200">
                Votre véhicule mérite le meilleur traitement
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Lavages Simples - nouveaux */}
      {simpleServices.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Lavages Simples
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Des prestations ciblées intérieur ou extérieur à prix accessibles
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {simpleServices.map((service) => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Formules Principales - LAVAGE renommé */}
      {principalServices.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Nos Formules Principales
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Deux formules complètes pour répondre à tous vos besoins de nettoyage
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {principalServices.map((service, index) => (
                <ServiceCard 
                  key={service.id} 
                  service={service} 
                  featured={index === 1} // LAVAGE 2 mis en avant
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services Spécialisés */}
      {specialServices.length > 0 && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Services Spécialisés
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Prestations expertes sur devis personnalisé
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {specialServices.map((service) => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Avis Google Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Ce que disent nos clients
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              La satisfaction de nos clients est notre priorité. Découvrez leurs témoignages authentiques.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <GoogleReviews 
              locationName="AutoSpa 77 - Ozoir-la-Ferrière"
              address="8 Rue Lavoisier, 77330 Ozoir-la-Ferrière"
            />
            <GoogleReviews 
              locationName="AutoSpa 77 - Croissy-Beaubourg"
              address="56 Boulevard Courcerin, 77183 Croissy-Beaubourg"
            />
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos Réalisations
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Découvrez quelques exemples de nos transformations avant/après
            </p>
          </div>
          
          {/* Gallery Content */}
          {galleryLoading && !gallery ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {[...Array(3)].map((_, index) => (
                <LoadingGalleryCard key={index} />
              ))}
            </div>
          ) : galleryError ? (
            <ErrorCard error={galleryError} onRetry={refetchGallery} />
          ) : featuredGallery.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {featuredGallery.map((item) => (
                <div key={item.id} className="bg-white rounded-xl shadow-lg overflow-hidden group hover:shadow-2xl transition-all duration-300">
                  <div className="relative">
                    <div className="grid grid-cols-2 gap-0">
                      {/* Avant */}
                      <div className="relative overflow-hidden">
                        <img 
                          src={item.beforeImage} 
                          alt={`Avant - ${item.description}`}
                          className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute top-2 left-2">
                          <Badge variant="secondary" className="bg-red-100 text-red-800 text-xs">
                            Avant
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Après */}
                      <div className="relative overflow-hidden">
                        <img 
                          src={item.afterImage} 
                          alt={`Après - ${item.description}`}
                          className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute top-2 right-2">
                          <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                            Après
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
                      <Button
                        onClick={() => setSelectedImage(item)}
                        className="opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300 bg-white text-gray-900 hover:bg-gray-100"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Voir en détail
                      </Button>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className="bg-blue-100 text-blue-800">{item.service}</Badge>
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">{item.description}</h3>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">Nos réalisations seront bientôt disponibles</p>
            </div>
          )}
          
          <div className="text-center mt-12">
            <Link to="/galerie">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
                Voir toute la galerie
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Modal pour voir les images en détail */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4">
          <div className="relative bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <Button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 z-10 bg-gray-900 text-white hover:bg-gray-800 rounded-full p-2"
            >
              <X className="w-5 h-5" />
            </Button>
            
            <div className="p-6">
              <div className="mb-4">
                <Badge className="bg-blue-100 text-blue-800 mb-2">{selectedImage.service}</Badge>
                <h3 className="text-2xl font-bold text-gray-900">{selectedImage.description}</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-semibold mb-2 text-red-600">Avant</h4>
                  <img 
                    src={selectedImage.beforeImage} 
                    alt={`Avant - ${selectedImage.description}`}
                    className="w-full rounded-lg shadow-lg"
                  />
                </div>
                <div>
                  <h4 className="text-lg font-semibold mb-2 text-green-600">Après</h4>
                  <img 
                    src={selectedImage.afterImage} 
                    alt={`Après - ${selectedImage.description}`}
                    className="w-full rounded-lg shadow-lg"
                  />
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                <p className="text-gray-600 mb-4">
                  Résultat obtenu grâce à nos techniques professionnelles
                </p>
                <Link to="/reservation">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                    Réserver le même service
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à redonner vie à votre véhicule ?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contactez-nous dès maintenant pour un devis gratuit et personnalisé
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/reservation">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg">
                Réserver en ligne
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="tel:0622360190">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 text-lg">
                <Phone className="w-5 h-5 mr-2" />
                06 22 36 01 90
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div className="space-y-2">
              <div className="text-4xl font-bold text-blue-600">200+</div>
              <div className="text-gray-600">Véhicules traités</div>
            </div>
            <div className="space-y-2">
              <div className="text-4xl font-bold text-blue-600">10+</div>
              <div className="text-gray-600">Années d'expérience</div>
            </div>
            <div className="space-y-2">
              <div className="text-4xl font-bold text-blue-600">2</div>
              <div className="text-gray-600">Adresses en 77</div>
            </div>
            <div className="space-y-2">
              <div className="text-4xl font-bold text-blue-600">4.9/5</div>
              <div className="text-gray-600">Note clients</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;